import { useMcpTool } from "../lib/useMcpTool";
import McpCard from "../components/McpCard";
import DataTable, { Column } from "../components/DataTable";
import { setChatContext } from "../lib/chatContext";

interface Client {
  id: string; name: string; company?: string; email?: string; phone?: string;
  health_score?: number; total_revenue?: number; revenue_30d?: number; last_engagement_days_ago?: number;
}

export default function ClientsPanel() {
  const clients = useMcpTool<Client[]>("business", "query_clients", { limit: 100 });
  const rows = Array.isArray(clients.data) ? clients.data : [];
  const cols: Column<Client>[] = [
    { header: "Restaurant", render: (c) => <span className="text-text font-medium">{c.company ?? c.name}</span> },
    { header: "Contact", render: (c) => <div><div>{c.name}</div><div className="text-caption text-text-dim">{c.email ?? c.phone ?? ""}</div></div> },
    { header: "Health", render: (c) => <Health s={c.health_score} /> },
    { header: "Lifetime", render: (c) => <span className="font-mono">{money(c.total_revenue)}</span>, className: "text-right" },
    { header: "30d", render: (c) => <span className="font-mono">{money(c.revenue_30d)}</span>, className: "text-right" },
    { header: "Last", render: (c) => <span className="font-mono text-caption">{c.last_engagement_days_ago != null ? `${c.last_engagement_days_ago}d` : "—"}</span> },
  ];
  return (
    <PanelScroll accentVar="--biome-business" title="Clients" subtitle="R&G · live from sage-business-mcp">
      <McpCard title={`Active clients${rows.length ? ` (${rows.length})` : ""}`} loading={clients.loading} error={clients.error} onRefresh={clients.refresh} accentVar="--biome-business">
        <DataTable columns={cols} rows={rows} empty="No clients returned." onRowClick={(c) => setChatContext({ label: `Client: ${c.company ?? c.name}`, detail: `${c.company ?? c.name} (contact ${c.name}${c.email ? `, ${c.email}` : ""}${c.phone ? `, ${c.phone}` : ""})\nHealth ${c.health_score ?? "?"} · lifetime ${money(c.total_revenue)} · 30d ${money(c.revenue_30d)}${c.last_engagement_days_ago != null ? ` · last engaged ${c.last_engagement_days_ago}d ago` : ""}` })} />
      </McpCard>
    </PanelScroll>
  );
}

function Health({ s }: { s?: number }) {
  if (s == null) return <span className="text-text-dim">—</span>;
  const c = s >= 70 ? "var(--success)" : s >= 50 ? "var(--warning)" : "var(--danger)";
  return <span className="font-mono" style={{ color: c }}>{s}</span>;
}
function money(v?: number) { return v == null ? "—" : v.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }); }

export function PanelScroll({ accentVar, title, subtitle, children }: { accentVar?: string; title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="h-full overflow-auto px-4 pt-5 pb-44">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-2 border-b-2 pb-2" style={{ borderColor: accentVar ? `var(${accentVar})` : "var(--border-strong)" }}>
          <h1 className="text-h1 font-serif text-text">{title}</h1>
          {subtitle && <span className="text-caption text-text-dim ml-2">{subtitle}</span>}
        </div>
        {children}
      </div>
    </div>
  );
}
